import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Header, Content } from './Styles';
import { StatusFilters } from './Styles';
import { Col, DatePicker, Input, message, Row, Select, Tooltip } from 'antd';
import client, { setStatusCodeCallback } from '../../../utils/Apollo/ApolloClient';
import { ApolloProvider } from '@apollo/client';
import moment from 'moment';
import StatusMonitoringContent from './StatusMonitoringContent';
import { getAllTeams } from '../../../utils/services/teamsAPI';
import { useSessionContext, checkUserAccess } from '../../../context/sessionContext';
import { STATUS_MONITORING, STATUS_MONITORING as trans } from './TranslationValues';
import { withTranslation } from 'react-i18next';
import { batchStatus, batchInitiation } from '../../../utils/constants/statusMonitoring';
import { getUser } from '../../../utils/services/userAPI';

const NAMESPACES = ['STATUS_MONITORING'];

const { RangePicker } = DatePicker;
const { Option } = Select;

const SIDEBAR_PAGINATION_HEIGHT = 52;
const BATCH_CARD_HEIGHT = 110;

const StatusMonitoring = ({ t }) => {
  const startDate = moment()
    .subtract(7, 'day')
    .startOf('day');
  const endDate = moment().endOf('day');
  const formattedStartDate = startDate.format('YYYY-MM-DDTHH:mm:ss');
  const formattedEndDate = endDate.format('YYYY-MM-DDTHH:mm:ss');
  const [filters, setFilters] = useState({
    filter: { page: 1, startDate: formattedStartDate, endDate: formattedEndDate },
  });
  const [cardCount, setCardCount] = useState(5);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const sidebarRef = useRef(null);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const isJobManager = checkUserAccess('client', ['JOBMANAGER'], userDetails);

  const fetchData = useCallback(async () => {
    try {
      const teams = await getAllTeams();
      if(isJobManager) {
        const users = await getUser();
        setUsers(users?.data);
        setTeams(teams?.data);
      } else {
        const userTeamIds = userDetails.resourceGroupPermission.write;
        const userTeams = teams?.data?.filter(team => userTeamIds.includes(team.id));
        setTeams(userTeams);
      }
    } catch (e) {
      console.log(e.error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const sidebarHeight = sidebarRef.current?.clientHeight;
    if (sidebarHeight) {
      const count = Math.floor((sidebarHeight - SIDEBAR_PAGINATION_HEIGHT) / BATCH_CARD_HEIGHT);
      setCardCount(count === 0 ? 5 : count);
      setFilters(prevFilter => ({
        filter: {
          ...prevFilter.filter,
          limit: count === 0 ? 5 : count,
        },
      }));
    }
  }, []);

  const onDateTimeRangeChange = dates => {
    const startDate = dates[0].format('YYYY-MM-DDTHH:mm:ss'); // Customize the format as needed
    const endDate = dates[1].format('YYYY-MM-DDTHH:mm:ss');
    setFilters(prevFilter => ({
      filter: {
        ...prevFilter.filter,
        page: 1,
        startDate,
        endDate,
      },
    }));
  };

  const handleSelectChange = (value, key) => {
    if(key === 'teamId' || key === 'triggeredBy' || key === 'status') {
      setFilterValues(prevFilterValues => ({
          ...prevFilterValues,
          [key]: value === '' ? null : value,
        }
      ));
    }
    
    setFilters(prevFilter => {
      const payload = {
        ...prevFilter.filter,
        page: 1,
        [key]: value === '' || (key === 'status' && value.length === 0) ? null : value,
      };
      if (key === 'initiatedFrom') {
        if (value === 'API') {
          if (filters.filter?.teamId) {
            payload.teamId = null;
          }
          if (filters.filter?.triggeredBy) {
            payload.triggeredBy = null;
          }
        } else {
          if (filterValues?.teamId) {
            payload.teamId = filterValues.teamId;
          }
          if (filterValues?.triggeredBy) {
            payload.triggeredBy = filterValues.triggeredBy;
          }
        }
      }
      return { filter: payload };
    });
  };

  return (
    <Container>
      <Header>
        <h1>{t(trans.STATUS_MONITORING_TITLE.key, trans.STATUS_MONITORING_TITLE.value)}</h1>
        <StatusFilters statusMonitoring>
          <Row
            type="flex"
            gutter={16}
            style={{ width: '100%', rowGap: '10px', justifyContent: 'flex-start' }}
          >
            <Col md={12} lg={8} xl={6}>
              <p className="label">
                {t(trans.DATE_FILTER_LABEL.key, trans.DATE_FILTER_LABEL.value)}
              </p>
              <RangePicker
                // disabledDate={disabledDate}
                inputReadOnly={true}
                allowClear={false}
                showTime={{
                  hideDisabledOptions: true,
                  defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('11:59:59', 'HH:mm:ss')],
                }}
                defaultValue={[startDate, endDate]}
                format="YYYY-MM-DD HH:mm:ss"
                onOk={onDateTimeRangeChange}
                disabledDate={current => current > moment().endOf('day')}
                style={{ width: '100%' }}
              />
            </Col>
            <Col md={12} lg={8} xl={6}>
              <p className="label">
                {t(trans.STATUS_FILTER_LABEL.key, trans.STATUS_FILTER_LABEL.value)}
              </p>
              <Select
                mode='multiple'
                style={{ width: '95%' }}
                value={filterValues?.status || []}
                placeholder={t(trans.STATUS_FILTER_LABEL.key, trans.STATUS_FILTER_LABEL.value)}
                onChange={val => handleSelectChange(val, 'status')}
                showArrow
                maxTagCount={1}
                maxTagPlaceholder={`+${filterValues.status?.length-1}`}
                allowClear
                filterOption={(input, option) => {
                  if (typeof option.props.children === 'string') {
                    return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  } else {
                    return (
                      option.props.children.props.title
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    );
                  }
                }}
              >
                {batchStatus(t, userDetails?.roles).map(status => (
                  <Option key={status.id} value={status.value}>
                    <Tooltip key={status.id} placement="topLeft" title={status.key}>
                      {status.key}
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Col>
            {teams?.length > 1 && <Col md={12} lg={8} xl={3}>
                  <p className="label">
                    {t(trans.TEAM_FILTER_LABEL.key, trans.TEAM_FILTER_LABEL.value)}
                  </p>
                  <Select
                    showSearch
                    style={{ width: '95%' }}
                    optionFilterProp="children"
                    defaultValue={t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                    onChange={val => handleSelectChange(val, 'teamId')}
                    filterOption={(input, option) => {
                      if (typeof option.props.children === 'string') {
                        return (
                          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        );
                      } else {
                        return (
                          option.props.children.props.title
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        );
                      }
                    }}
                    disabled={filters.filter['initiatedFrom'] === 'API'}
                  >
                    <Option value="" selected>
                      {t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                    </Option>
                    {teams.map(team => (
                      <Option key={team.id} value={team.id}>
                        <Tooltip key={team.id} placement="topLeft" title={team.name}>
                          {team.name}
                        </Tooltip>
                      </Option>
                    ))}
                  </Select>
                </Col>}
            {isJobManager && (
              <>
                <Col md={12} lg={8} xl={3}>
                  <p className="label">
                    {t(trans.INITIATION_FILTER_LABEL.key, trans.INITIATION_FILTER_LABEL.value)}
                  </p>
                  <Select
                    showSearch
                    style={{ width: '95%' }}
                    optionFilterProp="children"
                    defaultValue={t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                    onChange={val => handleSelectChange(val, 'initiatedFrom')}
                    filterOption={(input, option) => {
                      if (typeof option.props.children === 'string') {
                        return (
                          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        );
                      } else {
                        return (
                          option.props.children.props.title
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        );
                      }
                    }}
                  >
                    <Option value="" selected>
                      {t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                    </Option>
                    {batchInitiation(t).map(status => (
                      <Option key={status.id} value={status.key}>
                        <Tooltip key={status.id} placement="topLeft" title={status.value}>
                          {status.value}
                        </Tooltip>
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col md={12} lg={8} xl={3}>
                  <p className="label">
                    {t(trans.INITIATED_BY_LABEL.key, trans.INITIATED_BY_LABEL.value)}
                  </p>
                  <Select
                    showSearch
                    style={{ width: '95%' }}
                    optionFilterProp="children"
                    defaultValue={t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                    onChange={val => handleSelectChange(val, 'triggeredBy')}
                    filterOption={(input, option) => {
                      if (typeof option.props.children === 'string') {
                        return (
                          option.props.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        );
                      } else {
                        return (
                          option.props.children.props.title
                            ?.toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        );
                      }
                    }}
                    disabled={filters.filter['initiatedFrom'] === 'API'}
                  >
                    <Option value="" selected>
                      {t(trans.ALL_OPTION.key, trans.ALL_OPTION.value)}
                    </Option>
                    {users.map(user => (
                      <Option key={user.id} value={user.id}>
                        <Tooltip key={user.id} placement="topLeft" title={user.displayName}>
                          {user.displayName}
                        </Tooltip>
                      </Option>
                    ))}
                  </Select>
                </Col>
              </>
            )}
            <Col md={12} lg={8} xl={3}>
              <p className="label">
                {t(trans.JOB_NAME_FILTER_LABEL.key, trans.JOB_NAME_FILTER_LABEL.value)}
              </p>
              <Input
                placeholder={t(
                  trans.JOB_NAME_FILTER_LABEL.key,
                  trans.JOB_NAME_FILTER_LABEL.value,
                )}
                onChange={e => handleSelectChange(e.target.value, 'batchName')}
              />
            </Col>
          </Row>
        </StatusFilters>
      </Header>
      <Content ref={sidebarRef}>
        <StatusMonitoringContent
          filters={filters}
          setFilters={setFilters}
          cardCount={cardCount}
          users={users}
        />
      </Content>
    </Container>
  );
};

const StatusLiveMonitoring = ({ t }) => {
  useEffect(() => {
    const handleStatusCode = code => {
      code !== 4002 &&
        message.error(
          t(STATUS_MONITORING.GET_TOKEN_FAILED.key, STATUS_MONITORING.GET_TOKEN_FAILED.value),
        );
    };

    setStatusCodeCallback(handleStatusCode);

    return () => {
      setStatusCodeCallback(null);
    };
  }, []);

  return (
    <ApolloProvider client={client}>
      <StatusMonitoring t={t} />
    </ApolloProvider>
  );
};

export default withTranslation(NAMESPACES)(StatusLiveMonitoring);
